import type { Id } from "@convex/_generated/dataModel";
import type {
  CreditAllocation,
  CreditGrant,
  CreditSource,
} from "@convex/validators";
import type { ExtraPurchaseMetadata } from "./analytics";

/**
 * Pure credit-ledger logic — no `ctx`, no `ctx.db`, no `Date.now()`. Every
 * function here takes plain data (a clock value is always a parameter, never
 * read internally) and returns plain data, so it can be exhaustively unit
 * tested without `convex-test`. `convex/credits.ts` is the thin layer that
 * reads from the database, calls these, and writes the result — see that
 * file for the Convex functions and `convex/credits.test.ts` for the
 * integration tests that prove the wiring, auth boundaries, and
 * transactional behaviour.
 *
 * This module ports three plpgsql functions
 * (`supabase/schema/4_functions.sql`: `get_available_credits`,
 * `consume_credits_fefo`, `allocate_credits`) plus the source-classification
 * rule from `src/server/database/credit-ledger.ts:110-128`. Where SQL and
 * this file disagree, the SQL is the specification — comments below call out
 * every deliberate departure.
 */

// ---------------------------------------------------------------------
// Expiration constants.
// ---------------------------------------------------------------------

/**
 * How long a non-subscription credit batch stays spendable: extra
 * purchases (`extra_purchase`) and job-failure refunds (`refund`). Lives
 * here — the one module both `convex/` and `src/` may import — because the
 * value was duplicated three ways (`credit-ledger.ts`, the Stripe webhook's
 * `route.ts`, and implicitly in every refund path). Used by `resolveGrant`
 * below.
 */
export const EXTRA_CREDIT_EXPIRATION_MONTHS = 12; // 1 year

/**
 * How long a subscription-renewal batch stays spendable when the Stripe
 * price carries no `credits_expiration_days`, and the fixed lifetime of the
 * one-time signup-bonus grant. The one definition importable by both
 * `convex/` and `src/`. Used by `signupBonusAllocation` below and by
 * `model/stripe.ts` for the renewal default — `resolveGrant` never reads it
 * (a renewal grant carries its own `expirationDays`).
 */
export const DEFAULT_CREDITS_EXPIRATION_DAYS = 31;

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Credits granted when a trial subscription starts. Moved from
 * `AppConfig.trial.creditsIncluded` (which now re-exports this). */
export const TRIAL_CREDITS = 100;

/**
 * The one-time credits a user gets on signup (profile completion). There is
 * no monthly recharge — the stored names below are historical, from an old
 * version of the app, and are NOT renamed here because every existing row
 * carries them (MKVDEV-666 tracks the data migration). Refer to these
 * constants, never the literals.
 */
export const SIGNUP_BONUS_CREDITS = 30;
export const SIGNUP_BONUS_SOURCE =
  "free_monthly" as const satisfies CreditSource;
export const SIGNUP_BONUS_REFERENCE_TYPE = "free_recharge";

// ---------------------------------------------------------------------
// Liveness / balance — mirrors `get_available_credits`.
// ---------------------------------------------------------------------

export interface LiveBalanceBatch {
  remainingAmount: number;
  expiresAt: number;
}

/**
 * A batch counts as "live" — spendable, and included in the displayed
 * balance — when it still has a positive remainder AND has not expired.
 * Strict `>` on `expiresAt`: this is the exact predicate Task 6's live
 * parity gate checks against Postgres
 * (`remaining_amount > 0 AND expires_at > NOW()`). Nothing else in this
 * codebase may re-express this condition; every other liveness check
 * (the DB index, `availableBalance`, `planConsumption`) must reduce to
 * calling this or filtering with the same two comparisons.
 */
export function isLiveBatch(batch: LiveBalanceBatch, now: number): boolean {
  return batch.remainingAmount > 0 && batch.expiresAt > now;
}

/**
 * `SUM(remainingAmount)` over live batches. Mirrors `get_available_credits`
 * exactly, including that a non-live batch (expired, or already drained)
 * contributes nothing — regardless of why it isn't live.
 */
export function availableBalance(
  batches: readonly LiveBalanceBatch[],
  now: number,
): number {
  let total = 0;
  for (const batch of batches) {
    if (isLiveBatch(batch, now)) {
      total += batch.remainingAmount;
    }
  }
  return total;
}

// ---------------------------------------------------------------------
// hasRemaining — pins the one rule so it can never drift from
// remainingAmount, whichever mutation is doing the patching.
// ---------------------------------------------------------------------

export function deriveHasRemaining(remainingAmount: number): boolean {
  return remainingAmount > 0;
}

// ---------------------------------------------------------------------
// FEFO consumption planning — mirrors `consume_credits_fefo`'s pure
// (non-locking, non-writing) core.
// ---------------------------------------------------------------------

export interface ConsumptionBatch {
  id: Id<"creditBatches">;
  remainingAmount: number;
  expiresAt: number;
}

export interface ConsumptionAllocation {
  batchId: Id<"creditBatches">;
  consumed: number;
  expiresAt: number;
  /** The batch's `remainingAmount` after this consumption — what the
   * caller must patch it to (together with `deriveHasRemaining` of the
   * same value). Not part of the plpgsql's `batches_affected` shape or
   * this port's external `batchesAffected` return value; internal to the
   * plan so the handler never has to recompute `remainingAmount - take`
   * itself. */
  remainingAfter: number;
}

export interface ConsumptionPlan {
  success: boolean;
  consumed: number;
  allocations: ConsumptionAllocation[];
}

/**
 * Builds the FEFO (first-expiring-first-out) plan for consuming `amount`
 * credits from the given live batches. Pure: does not touch the database,
 * and does not itself decide anything about idempotency or references —
 * those are the caller's job.
 *
 * Contract, ported from `consume_credits_fefo`:
 * - `amount <= 0` fails (not an error) with nothing consumed.
 * - if the live balance is less than `amount`, fails with nothing consumed
 *   — no partial consumption.
 * - otherwise walks batches in `expiresAt` ascending order, taking
 *   `min(batch.remainingAmount, remaining)` from each until satisfied.
 *
 * Two intentional departures from a literal SQL port, both defensive
 * tightenings rather than behaviour changes for any well-formed input:
 *
 * 1. **Does not trust the input batches to already be sorted.** It sorts by
 *    `expiresAt` ascending itself. `Array.prototype.sort` is a stable sort
 *    (guaranteed since ES2019), so two batches with an identical
 *    `expiresAt` are drawn from in the order they appear in the input
 *    array. Postgres's `ORDER BY expires_at ASC` has no tiebreak clause,
 *    so which of two same-instant batches is drained first is technically
 *    unspecified there — pinning it to input order here is a deliberate
 *    tightening for determinism, not a port of a Postgres guarantee that
 *    does not exist.
 * 2. **Filters out any batch with `remainingAmount <= 0` before summing or
 *    walking.** The same-patch `hasRemaining`/`remainingAmount` invariant
 *    (see `deriveHasRemaining`) should make "live per the DB index query,
 *    but already drained" structurally impossible, but if it ever happens
 *    anyway this function does not let such a batch corrupt the balance
 *    sum (a negative `remainingAmount` would undercount it) or appear as a
 *    zero/negative entry in `allocations`. It is silently skipped, exactly
 *    as if it were not live.
 */
export function planConsumption(
  batches: readonly ConsumptionBatch[],
  amount: number,
): ConsumptionPlan {
  // `!(amount > 0)` also rejects NaN, which would otherwise pass the balance
  // check and plan NaN into every batch's remainingAmount.
  if (!(amount > 0)) {
    return { success: false, consumed: 0, allocations: [] };
  }

  const spendable = batches.filter((batch) => batch.remainingAmount > 0);
  const balance = spendable.reduce(
    (sum, batch) => sum + batch.remainingAmount,
    0,
  );
  if (balance < amount) {
    return { success: false, consumed: 0, allocations: [] };
  }

  const ordered = [...spendable].sort((a, b) => a.expiresAt - b.expiresAt);

  let remaining = amount;
  const allocations: ConsumptionAllocation[] = [];
  for (const batch of ordered) {
    if (remaining <= 0) break;
    const take = Math.min(batch.remainingAmount, remaining);
    allocations.push({
      batchId: batch.id,
      consumed: take,
      expiresAt: batch.expiresAt,
      remainingAfter: batch.remainingAmount - take,
    });
    remaining -= take;
  }

  return { success: true, consumed: amount - remaining, allocations };
}

// ---------------------------------------------------------------------
// Metadata shaping — where the plpgsql's batch-vs-transaction asymmetry
// lives. `allocate_credits` writes
// `jsonb_build_object('reference_type', p_reference_type, 'reference_id',
// p_reference_id) || p_metadata` to the BATCH, but bare `p_metadata` to the
// TRANSACTION. Postgres's `||` on jsonb objects has the right-hand side win
// on a key collision — both functions below preserve that precedence.
// ---------------------------------------------------------------------

export function buildAllocationBatchMetadata(
  referenceType: string | undefined,
  referenceId: string | undefined,
  callerMetadata: Record<string, unknown>,
): Record<string, unknown> {
  return {
    reference_type: referenceType ?? null,
    reference_id: referenceId ?? null,
    ...callerMetadata,
  };
}

/**
 * Deliberately NOT reference-merged — see the module-level comment above.
 * This is effectively an identity function over `callerMetadata`, named
 * and exported so the asymmetry with `buildAllocationBatchMetadata` is a
 * visible, tested decision rather than an easy-to-miss detail buried in a
 * handler.
 */
export function buildAllocationTransactionMetadata(
  callerMetadata: Record<string, unknown>,
): Record<string, unknown> {
  return { ...callerMetadata };
}

/**
 * The single consumption transaction's metadata:
 * `jsonb_build_object('batches', v_batches_affected) || p_metadata` in the
 * plpgsql — caller metadata wins over a `batches` key collision, matching
 * the same right-hand-wins precedence as the allocation batch metadata.
 */
export function buildConsumptionTransactionMetadata(
  batches: readonly {
    batchId: Id<"creditBatches">;
    consumed: number;
    expiresAt: number;
  }[],
  callerMetadata: Record<string, unknown>,
): Record<string, unknown> {
  return { batches, ...callerMetadata };
}

// ---------------------------------------------------------------------
// Grant policy — the ONLY place a grant's source, expiry, dedup reference
// and metadata are chosen. Callers describe what happened (a `CreditGrant`);
// this turns it into the ledger row. Every kind except `admin` must yield
// BOTH reference fields: `allocate` only deduplicates when both are present.
// ---------------------------------------------------------------------

/** Calendar months in UTC — the same result `dayjs().add(n, "months")` gave
 * on the UTC servers this replaces, including end-of-month clamping (Jan 31
 * + 1 month → Feb 28/29). Plain `Date` math rather than dayjs: `src/config.ts`
 * imports this module, so a dependency here lands in every client bundle. */
function addMonths(at: number, months: number): number {
  const date = new Date(at);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + months;
  const lastDayOfTargetMonth = new Date(
    Date.UTC(year, month + 1, 0),
  ).getUTCDate();
  date.setUTCFullYear(
    year,
    month,
    Math.min(date.getUTCDate(), lastDayOfTargetMonth),
  );
  return date.getTime();
}

export function resolveGrant(
  grant: CreditGrant,
  now: number,
): CreditAllocation {
  switch (grant.kind) {
    case "subscription_renewal":
      return {
        userId: grant.userId,
        source: "subscription",
        amount: grant.totalCredits,
        expiresAt: grant.paidAt + grant.expirationDays * MS_PER_DAY,
        referenceType: "subscription_renewal",
        referenceId: grant.invoiceId,
        metadata: { invoice_id: grant.invoiceId },
      };
    case "extra_purchase":
      return {
        userId: grant.userId,
        source: "extra_purchase",
        amount: grant.amount,
        expiresAt: addMonths(now, EXTRA_CREDIT_EXPIRATION_MONTHS),
        referenceType: "stripe_session",
        referenceId: grant.sessionId,
        // Read back by `creditsGrantedEvents` for `credits_purchased`.
        metadata: {
          stripe_payment_intent_id: grant.paymentIntentId ?? null,
          price_usd: grant.priceUsd ?? null,
        } satisfies ExtraPurchaseMetadata,
      };
    case "trial":
      return {
        userId: grant.userId,
        source: "trial",
        amount: TRIAL_CREDITS,
        expiresAt: grant.trialEndsAt,
        referenceType: "trial_start",
        referenceId: grant.subscriptionId,
      };
    case "refund":
      return {
        userId: grant.userId,
        source: "refund",
        amount: grant.amount,
        expiresAt: addMonths(now, EXTRA_CREDIT_EXPIRATION_MONTHS),
        referenceType: grant.reference.type,
        referenceId: grant.reference.id,
        metadata: { refund_reason: grant.reason },
      };
    case "admin":
      // No referenceId, deliberately: every admin grant must allocate, and
      // inventing an id would collapse two same-size grants into one.
      return {
        userId: grant.userId,
        source: "admin_grant",
        amount: grant.amount,
        expiresAt: now + grant.expirationDays * MS_PER_DAY,
        referenceType: "admin_grant",
        metadata: { adminId: grant.adminId, reason: grant.reason },
      };
    default: {
      const exhaustive: never = grant;
      throw new Error(`Unhandled credit grant ${JSON.stringify(exhaustive)}`);
    }
  }
}

/**
 * The signup bonus allocation. Not a `CreditGrant` kind on purpose: its
 * once-per-user-for-life guard lives in `grantSignupBonus`, and a union
 * member would let `api.credits.grant` skip it.
 */
export function signupBonusAllocation(
  userId: string,
  now: number,
): CreditAllocation {
  return {
    userId,
    source: SIGNUP_BONUS_SOURCE,
    amount: SIGNUP_BONUS_CREDITS,
    expiresAt: now + DEFAULT_CREDITS_EXPIRATION_DAYS * MS_PER_DAY,
    referenceType: SIGNUP_BONUS_REFERENCE_TYPE,
  };
}

// ---------------------------------------------------------------------
// Credits-breakdown source classification — mirrors
// `CreditLedger.getCreditsBreakdown` (`src/server/database/credit-ledger.ts:
// 110-128`). No Convex function in this phase computes a breakdown from raw
// batches — the credits UI reads pre-computed totals from `availableCredits`
// / `availableCreditsForUser` instead — but `src/` may import from
// `convex/`, so this is the one definition Task 11's UI can share instead of
// copying the source list a second time.
// ---------------------------------------------------------------------

export type CreditBreakdownBucket = "subscription" | "extra";

const SUBSCRIPTION_LIKE_SOURCES: readonly CreditSource[] = [
  "subscription",
  "trial",
  SIGNUP_BONUS_SOURCE,
  "refund",
];

export function classifyCreditSource(
  source: CreditSource,
): CreditBreakdownBucket {
  return SUBSCRIPTION_LIKE_SOURCES.includes(source) ? "subscription" : "extra";
}
